/**
 * The full-window settings layer (docs/design/visual-overhaul.md §6.9,
 * §7.24; WP5): User settings and Server settings share it. It covers the
 * window below the title bar; the nav region (column 1) scrolls on its own,
 * and one scroller holds the page and the sticky close button (columns 2
 * and 3). The layer itself never scrolls, so no page can be cut off (L1,
 * I1, I2). Escape closes it; the tabs are a vertical tablist whose current
 * tab takes the focus when it opens (I29).
 *
 * Phones and tablets (§12.7 C1, C2): on a phone the nav is the first
 * full-screen pane (with a close) and a section opens as a pane pushed over
 * it with Back, as iOS Settings does; a list's editor (channels, roles) is a
 * pane pushed over its list on a phone and a portrait tablet. The layer is
 * one entry on the back stack whose Back takes the deepest step first (the
 * editor, then the page, then the layer), so the order never depends on
 * which effect ran first. Desktop: unchanged.
 */
import {
  createContext,
  Fragment,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
  type RefObject,
} from 'react';
import { createPortal } from 'react-dom';
import { goBack, pushBack } from '@neosocial/ui/back';
import { isCompact, readLayout, useLayout } from '@neosocial/ui/layout';
import { useT } from '../i18n';
import { Btn, Icon, type IconName } from '../ui/kit';
import { animatesExits, portalRoot, pushEscape } from '../ui/overlay';

/** Phone: the pane of the layer on screen, its nav list or the page pushed over it. */
export type SetPane = 'nav' | 'page';

interface SetPaneApi {
  /** A tap on the nav's current tab: its page slides in (phone). */
  openPage(): void;
  /** A step deeper than the page (an editor over its list): Back runs it first. Returns the stop. */
  step(onBack: () => void): () => void;
}
const SetPaneCtx = createContext<SetPaneApi | null>(null);

/**
 * What the layer's Back does, deepest first: the innermost step (an editor
 * pushed over its list), then the page (back to the nav list, phone), then
 * the layer itself.
 */
export function setBackStep(s: {
  steps: number;
  phone: boolean;
  pane: SetPane;
}): 'step' | 'nav' | 'close' {
  if (s.steps > 0) return 'step';
  if (s.phone && s.pane === 'page') return 'nav';
  return 'close';
}

/**
 * A list's editor as a pushed pane (phones and portrait tablets, §12.7 C2):
 * while `open`, Back (the system's or the bar's) runs `onBack` before
 * anything else. `onBack` may keep the editor (unsaved edits): the step
 * then stays.
 */
export function useSetStep(open: boolean, onBack: () => void): void {
  const api = useContext(SetPaneCtx);
  const ref = useRef(onBack);
  useEffect(() => {
    ref.current = onBack;
  });
  useEffect(() => {
    if (!open || !api) return;
    return api.step(() => ref.current());
  }, [open, api]);
}

/** Phones and portrait tablets show a list or its editor, never both (§12.7 C2). */
export function useSplitCompact(): boolean {
  return isCompact(useLayout());
}

/**
 * The editor's way back to its list on a portrait tablet, which has no pane
 * bar (a phone's bar has the Back).
 */
export function SplitBack({ label }: { label: string }) {
  const { t } = useT();
  const l = useLayout();
  if (!(l.layout === 'tablet' && l.nav === 'drawer')) return null;
  return (
    <button type="button" className="ns-adm-back" onClick={() => goBack()}>
      <Icon name="chev-left" size="m" />
      <span>
        <span className="nx-sr">{t('ns.pane.back') + ': '}</span>
        {label}
      </span>
    </button>
  );
}

// how the layer was opened: a pointer opening shows no focus ring on the tab
let modality: 'pointer' | 'keyboard' = 'keyboard';
if (typeof document !== 'undefined') {
  document.addEventListener('pointerdown', () => (modality = 'pointer'), true);
  document.addEventListener('keydown', () => (modality = 'keyboard'), true);
}

const FOCUSABLE =
  'button:not([disabled]):not([tabindex="-1"]), [href], input:not([disabled]):not([type="hidden"]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

function focusables(el: HTMLElement): HTMLElement[] {
  return [...el.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
    (x) => x.offsetParent !== null || x === document.activeElement,
  );
}

/**
 * The exit (§6.9: opacity over 120ms): a copy that fades. It is not the
 * layer: no hook classes, ids, names or roles, so nothing can find or use it.
 */
function ghostOut(el: HTMLElement | null): void {
  if (!el || !el.isConnected || !animatesExits()) return;
  const g = el.cloneNode(true) as HTMLElement;
  g.className = 'nx-set-ghost';
  g.setAttribute('aria-hidden', 'true');
  g.inert = true;
  for (const n of [g, ...g.querySelectorAll<HTMLElement>('*')])
    for (const a of ['id', 'name', 'role', 'for', 'autocomplete', 'aria-labelledby', 'aria-label'])
      n.removeAttribute(a);
  document.body.appendChild(g);
  const done = () => g.remove();
  g.addEventListener('animationend', done, { once: true });
  setTimeout(done, 200);
}

/**
 * The unsaved-changes bar (§7.24): sticky 16px above the page's foot, so
 * Save is always in reach, however long the page.
 */
export function UnsavedBar({
  onReset,
  onSave,
  busy,
  saveDisabled,
  saveLabel,
  text,
}: {
  onReset?: () => void;
  onSave(): void;
  busy?: boolean;
  saveDisabled?: boolean;
  saveLabel?: string;
  /** The bar's words; null for a form that is new (a role being created). */
  text?: string | null;
}) {
  const { t } = useT();
  const words = text === undefined ? t('roles.unsaved') : text;
  return (
    <div className="ns-savebar" role="region" aria-label={t('roles.unsaved')}>
      {words ? <span className="ns-savebar-t">{words}</span> : null}
      <span className="ns-fill" />
      {onReset ? (
        <Btn size="sm" kind="ghost" onClick={onReset} disabled={busy}>
          {t('roles.reset')}
        </Btn>
      ) : null}
      <Btn
        kind="primary"
        size="sm"
        disabled={busy || saveDisabled}
        busy={busy}
        onClick={() => onSave()}
      >
        {saveLabel ?? t('c.save')}
      </Btn>
    </div>
  );
}

export interface NavTab {
  id: string;
  icon: IconName;
  label: string;
  /** Ownership: last, in the danger ink, after a line (§6.9). */
  danger?: boolean;
}
export interface NavGroup {
  title?: string;
  tabs: NavTab[];
}

/** The vertical tablist: ↑/↓ (and Home/End) move between tabs; Enter or a click opens one. */
export function SetNav({
  label,
  groups,
  current,
  onPick,
  listCls,
}: {
  label: string;
  groups: NavGroup[];
  current: string | undefined;
  onPick(id: string): void;
  /** An extra class on the tablist (Server settings keeps `ns-adm-tabs`). */
  listCls?: string;
}) {
  const pane = useContext(SetPaneCtx);
  const phone = useLayout().layout === 'phone';
  const first = groups[0]?.tabs[0]?.id;
  const stop = groups.some((g) => g.tabs.some((x) => x.id === current)) ? current : first;
  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const keys = ['ArrowDown', 'ArrowUp', 'Home', 'End'];
    if (!keys.includes(e.key)) return;
    const tabs = [...e.currentTarget.querySelectorAll<HTMLElement>('[role="tab"]')];
    if (!tabs.length) return;
    e.preventDefault();
    const at = tabs.indexOf(document.activeElement as HTMLElement);
    const n = tabs.length;
    const next =
      e.key === 'Home'
        ? 0
        : e.key === 'End'
          ? n - 1
          : at < 0
            ? 0
            : (at + (e.key === 'ArrowDown' ? 1 : -1) + n) % n;
    tabs[next]?.focus();
  };
  return (
    <nav className="nx-set-nav" aria-label={label}>
      <div
        className={'nx-set-tabs' + (listCls ? ' ' + listCls : '')}
        role="tablist"
        aria-orientation="vertical"
        aria-label={label}
        onKeyDown={onKey}
      >
        {groups.map((g, gi) => (
          <Fragment key={g.title ?? gi}>
            {g.title ? (
              <div className="nx-set-gh" role="presentation">
                {g.title}
              </div>
            ) : gi > 0 ? (
              <div className="nx-set-sep" role="presentation" />
            ) : null}
            {g.tabs.map((x) => {
              const on = x.id === current;
              return (
                <button
                  key={x.id}
                  type="button"
                  role="tab"
                  className={'nx-set-tab' + (x.danger ? ' danger' : '')}
                  aria-selected={on}
                  aria-current={on ? 'page' : undefined}
                  // the tablist is one stop: Tab goes on to the page (roving focus)
                  tabIndex={x.id === stop ? 0 : -1}
                  data-tab={x.id}
                  onClick={() => {
                    // phone: the page already chosen slides in again
                    if (x.id === current) pane?.openPage();
                    onPick(x.id);
                  }}
                >
                  <Icon name={x.icon} size="m" />
                  <span className="nx-set-tab-l">{x.label}</span>
                  {phone ? <Icon name="chev-right" cls="nx-set-tab-go" /> : null}
                </button>
              );
            })}
          </Fragment>
        ))}
      </div>
    </nav>
  );
}

/**
 * The layer. `nav` is column 1; `children` is the page (column 2), beside
 * the close button (column 3), both in one scroller (`bodyRef`). `fill`
 * makes the page exactly the scroller's height, for the split editors whose
 * list and editor scroll on their own (Channels, Roles).
 *
 * Phone: `page` is the shown page's id (a new one slides its pane in),
 * `pageTitle` the page bar's title, and `listFirst` opens the layer on its
 * nav list (the general entries) rather than on the page (a deep link).
 */
export function SetLayer({
  cls,
  label,
  closeLabel,
  onClose,
  nav,
  bodyRef,
  fill,
  page,
  pageTitle,
  listFirst,
  children,
}: {
  cls: string;
  label: string;
  closeLabel?: string;
  onClose(): void;
  nav: ReactNode;
  bodyRef?: RefObject<HTMLDivElement | null>;
  fill?: boolean;
  page?: string;
  pageTitle?: string;
  listFirst?: boolean;
  children: ReactNode;
}) {
  const { t } = useT();
  const ref = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);
  useEffect(() => {
    closeRef.current = onClose;
  });
  const lay = useLayout();
  const phone = lay.layout === 'phone';
  // phones: the nav list, or the page pushed over it
  const [pane, setPane] = useState<SetPane>(() =>
    readLayout().layout === 'phone' && listFirst ? 'nav' : 'page',
  );
  // another tab picked in the nav: its page slides in
  const [shown, setShown] = useState(page);
  if (shown !== page) {
    setShown(page);
    setPane('page');
  }
  const paneRef = useRef(pane);
  useLayoutEffect(() => {
    paneRef.current = pane;
  }, [pane]);
  const steps = useRef<Array<() => void>>([]);
  const api = useMemo<SetPaneApi>(
    () => ({
      openPage: () => setPane('page'),
      step: (fn) => {
        steps.current.push(fn);
        return () => {
          const i = steps.current.indexOf(fn);
          if (i >= 0) steps.current.splice(i, 1);
        };
      },
    }),
    [],
  );
  // phones and tablets: one entry on the back stack, the deepest step first
  const backOn = lay.layout !== 'desktop';
  useEffect(() => {
    if (!backOn) return;
    let pop: (() => void) | null = null;
    const onBack = () => {
      // armed again first: a confirm that the step opens goes above it
      pop = pushBack(onBack);
      const step = setBackStep({
        steps: steps.current.length,
        phone: readLayout().layout === 'phone',
        pane: paneRef.current,
      });
      if (step === 'step') steps.current.at(-1)?.();
      else if (step === 'nav') setPane('nav');
      else closeRef.current();
    };
    pop = pushBack(onBack);
    return () => pop?.();
  }, [backOn]);
  // the exit runs on a copy (the layer itself is gone with its parent)
  useLayoutEffect(() => {
    const el = ref.current;
    return () => ghostOut(el);
  }, []);
  useEffect(() => {
    const restore = document.activeElement as HTMLElement | null;
    const byPointer = modality === 'pointer';
    const off = pushEscape(() => closeRef.current());
    const raf = requestAnimationFrame(() => {
      const el = ref.current;
      if (!el) return;
      // the current tab, not the first one (I29); a phone's page: its Back
      const f =
        (el.dataset.pane === 'page'
          ? el.querySelector<HTMLElement>('.nx-set-body .nx-set-bar-b')
          : null) ??
        el.querySelector<HTMLElement>('.nx-set-tab[aria-current="page"]') ??
        el.querySelector<HTMLElement>('.nx-set-tab') ??
        el;
      f.focus({
        preventScroll: true,
        ...(byPointer ? { focusVisible: false } : {}),
      } as FocusOptions);
    });
    return () => {
      cancelAnimationFrame(raf);
      off();
      if (restore && document.contains(restore)) restore.focus({ preventScroll: true });
    };
  }, []);
  // phone: the focus follows the pane (the page's Back, the nav's current tab)
  const firstPane = useRef(true);
  useEffect(() => {
    if (firstPane.current) {
      firstPane.current = false;
      return;
    }
    if (!phone) return;
    const el = ref.current;
    const f =
      pane === 'page'
        ? el?.querySelector<HTMLElement>('.nx-set-body .nx-set-bar-b')
        : el?.querySelector<HTMLElement>('.nx-set-tab[aria-current="page"]');
    f?.focus({ preventScroll: true, focusVisible: false } as FocusOptions);
  }, [pane, phone]);
  const close = closeLabel ?? t('set.close');
  return createPortal(
    <div
      ref={ref}
      className={cls}
      role="dialog"
      aria-modal="true"
      aria-label={label}
      tabIndex={-1}
      data-state="open"
      data-fill={fill ? '' : undefined}
      data-pane={phone ? pane : undefined}
      onKeyDown={(e) => {
        if (e.key !== 'Tab') return;
        const list = focusables(e.currentTarget);
        if (!list.length) return;
        const first = list[0]!;
        const last = list[list.length - 1]!;
        if (
          e.shiftKey &&
          (document.activeElement === first || document.activeElement === e.currentTarget)
        ) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }}
    >
      <SetPaneCtx.Provider value={api}>
        <div className="nx-set-side nx-scroll">
          {phone ? (
            <div className="nx-set-bar">
              <h2 className="nx-set-bar-t">{label}</h2>
              <button
                type="button"
                className="nx-set-bar-b"
                aria-label={close}
                onClick={() => closeRef.current()}
              >
                <Icon name="x" size="m" />
              </button>
            </div>
          ) : null}
          {nav}
        </div>
        <div className="nx-set-body nx-scroll-read" ref={bodyRef}>
          {phone ? (
            <div className="nx-set-bar">
              <button
                type="button"
                className="nx-set-bar-b"
                aria-label={t('ns.pane.back')}
                onClick={() => goBack()}
              >
                <Icon name="chev-left" size="m" />
              </button>
              <h2 className="nx-set-bar-t" title={pageTitle}>
                {pageTitle}
              </h2>
            </div>
          ) : null}
          {children}
          {!phone ? (
            <div className="nx-set-close">
              <button
                type="button"
                className="nx-set-x"
                aria-label={close}
                aria-keyshortcuts="Escape"
                onClick={() => closeRef.current()}
              >
                <Icon name="x" size="m" />
              </button>
              {/* a finger has no Escape key */}
              {lay.pointer === 'fine' ? (
                <kbd className="nx-kbd" aria-hidden="true">
                  {t('set.esc')}
                </kbd>
              ) : null}
            </div>
          ) : null}
        </div>
      </SetPaneCtx.Provider>
    </div>,
    portalRoot(),
  );
}
